import { get, debounce } from "lodash";

import React, { Component } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import { yupToFormErrors } from "formik";

import ValidationMessage from "../forms/messages/ValidationMessage.jsx";
import WizardPages from "./WizardPages.jsx";

import { ESTIMATE_DIMENSIONS } from "../../state/reducers/projectWizard/projectWizardConstants.js";
import { DEFAULT_SCHEDULE_KEYS } from "../../state/reducers/schedules/configs/defaultSchedulesData.js";

import { getEstimateScheduleArray } from "../../state/reducers/schedules/scheduleUtils.js";
import { projectWizardUpdateEstimate } from "../../state/reducers/projectWizard/projectWizardActions.js";

import { selectLimits } from "../../state/reducers/account/limitSelector.js";
import { selectEstimateAvailability } from "../../state/reducers/projectWizard/projectWizardSelectors.js";
import { selectCopy } from "../../state/reducers/locale/copySelector.js";

class ProjectWizardSteppedForm extends Component {
  static propTypes = {
    schedule: PropTypes.object,
    showValidation: PropTypes.bool.isRequired,
    onCancel: PropTypes.func.isRequired,
    onReset: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    updateEstimate: PropTypes.func.isRequired,
    project: PropTypes.object,
    config: PropTypes.object.isRequired,
    provider: PropTypes.object,
    region: PropTypes.object,
    violationErrors: PropTypes.object,
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,

    limits: PropTypes.object,
    copy: PropTypes.func.isRequired
  };

  static defaultProps = {
    showValidation: false
  };

  static ESTIMATE_DEBOUNCE = 1000;

  constructor(props) {
    super(props);

    const projectId = get(props, "project.@id", null);

    this.state = {
      projectId, // dangerous needs refactoring
      cancelWizard: false,
      showMessages: false,
      initialValues: {
        ignoreChangeInSideEffect: false,
        schedule: DEFAULT_SCHEDULE_KEYS.OFFICE,
        dimension: ESTIMATE_DIMENSIONS.WEEKLY,
        projectId, // dangerous needs refactoring
        ...props.config.initialValues // initialValues now all come from the config
      },
      panelCompleteStates: {},
      stepCompleteStates: [],
      prevProviderValue: null,
      prevRegionValue: null
    };
  }

  static getDerivedStateFromProps(props, state) {
    if (
      props.provider !== state.prevProviderValue ||
      props.region !== state.prevRegionValue
    ) {
      const initialValues = {
        ...state.initialValues,
        provider: props.provider["@id"],
        region: props.region["@id"]
      };

      return {
        initialValues,
        prevProviderValue: props.provider["@id"],
        prevRegionValue: props.region["@id"]
      };
    }

    return null;
  }

  componentWillUnmount() {
    if (this.props.estimateAvailability) this.debounceEstimateChange.cancel();
  }

  // -- HANDLERS -- //

  handleReset = () => {
    this.setState({ stepCompleteStates: [] }, this.props.onReset());
  };

  // all of this jiggery pokery is required to manage the estimate updates and panels completion states
  // dual validation is nasty but at the each key press will currently call onFormikChange twice
  // once for the keypress once for the validation isValid will not be updated on the first call.
  doEstimate = (prevFormikProps, formikProps) => {
    const {
      project,
      config: { estimateConditions, steps },
      copy
    } = this.props;

    const messages = copy("forms.validation_requirements");

    // -- Estimate call logic
    if (formikProps.values.ignoreChangeInSideEffect !== true) {
      const conditions = estimateConditions.map(id => {
        const step = steps.find(element => element.id === id);
        return step
          .validationSchema(messages)
          .validate(formikProps.values, { abortEarly: false });
      });

      Promise.all(conditions)
        .then(values => {
          const schedules = getEstimateScheduleArray(
            formikProps.values.schedule
          );

          const cleanedValues = { ...formikProps.values };
          delete cleanedValues.schedule;
          delete cleanedValues.ignoreChangeInSideEffect;

          this.props.updateEstimate(cleanedValues, schedules, project);
        })
        .catch(err => {
          // console.warn(
          //   "<ProjectWizardSteppedForm doEstimate> Validation failed",
          //   err
          // );
        });
    }
  };

  debounceEstimateChange = debounce(
    this.doEstimate,
    ProjectWizardSteppedForm.ESTIMATE_DEBOUNCE
  );

  updateStepCompletedStates = (pageNumber, value) => {
    const { stepCompleteStates } = this.state;

    const newStepCompletedStates = [...stepCompleteStates];
    newStepCompletedStates[pageNumber] = value;

    this.setState({
      stepCompleteStates: newStepCompletedStates
    });
  };

  // -- VALIDATION -- //

  validate = (values, pageNumber) => {
    const {
      config: { steps },
      copy
    } = this.props;

    const step = steps[pageNumber];

    if (!step || !step.validationSchema) return {};

    const messages = copy("forms.validation_requirements");

    return step
      .validationSchema(messages)
      .validate(values, { abortEarly: false })
      .then(result => {
        this.updateStepCompletedStates(pageNumber, step.id);
        return {};
      })
      .catch(err => {
        this.updateStepCompletedStates(pageNumber, undefined);

        if (err.name === "ValidationError") {
          return yupToFormErrors(err);
        } else {
          // We throw any other errors
          console.warn(
            `Warning: An unhandled error was caught during validation in <Wizard validate />`,
            err
          );
          throw err;
        }
      });
  };

  // -- RENDER -- //

  // handling this outside of Formik as the message should be shown as soon as submit has been clicked
  // not just on successfull Formik submission, or on failed validation check.
  renderMessage = formikProps => {
    if (this.state.showMessages !== true) return null;

    const valid =
      formikProps.isValid && !this.props.violationErrors ? true : false;

    return <ValidationMessage className="mid-panel-message" valid={valid} />;
  };

  render() {
    const { initialValues, stepCompleteStates } = this.state;
    const {
      config,
      violationErrors,
      estimateAvailability,
      limits,
      provider,
      region,
      onCancel,
      onSubmit,
      history,
      match
    } = this.props;

    const onChange = estimateAvailability
      ? this.debounceEstimateChange
      : undefined;

    return (
      <WizardPages
        config={config}
        initialValues={initialValues} // required by semantic-ui react
        limits={limits}
        provider={provider}
        region={region}
        validate={this.validate}
        onCancel={onCancel}
        onReset={this.handleReset}
        onSubmit={onSubmit}
        stepCompleteStates={stepCompleteStates}
        onChange={onChange}
        violationErrors={violationErrors}
        history={history}
        match={match}
      />
    );
  }
}

const mapStateToProps = (state, props) => ({
  estimateAvailability: selectEstimateAvailability(state, props),
  limits: selectLimits(state, props),
  copy: selectCopy(state)
});

const mapDispatchToProps = dispatch => ({
  updateEstimate: (data, schedules, project) =>
    dispatch(projectWizardUpdateEstimate(data, schedules, project))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ProjectWizardSteppedForm);
